import qs from 'querystringify';
import assign from 'lodash/assign';
import merge from 'lodash/merge';
import { LocalStorage as Cache } from 'ttl-localstorage';
import {
  CLIENT_TYPE_HEADER,
  CLIENT_TYPE,
} from '@upstart/harmony/lib/constants';
import { cloneDeep } from 'lodash';

import {
  CLIENT_CODENAMES,
  X_UAR_HTTP_HEADERS,
} from '@upstart/harmony/lib/legos/constants/system';
import { MessageType } from '@upstart/harmony/lib/legos/websiteTypes';
import { getToken } from '../util/tokenUtils';
import { broadcastNewSessionTimeout } from '../util/sessionTimeout';
import getDealIdHeader from '../util/dealIdHeader';
import store from '../redux/store';
import { changeSystemStatus } from '../redux/global/actions';
import { forwardSessionTimeoutUpdateToCC } from '../util/commandCenter';
import { PageMessage } from '../components/GlobalUI';

export const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ?? 'https://autoretail-api.upstart.com';
const SYSTEM_STATUS_URL = process.env.REACT_APP_SYSTEM_STATUS_URL;

const DEFAULT_HEADERS = {
  [CLIENT_TYPE_HEADER]: CLIENT_TYPE.IN_STORE,
  [X_UAR_HTTP_HEADERS['X-UAR-Client-Codename']]: CLIENT_CODENAMES.CHAIRMAN,
};
const CONTENT_TYPE_HEADER = {
  'Content-Type': 'application/json',
};

const SESSION_DURATION_HEADER = X_UAR_HTTP_HEADERS['X-UAR-Session-Duration'];

Cache.synchronous = true;
Cache.timeoutInSeconds = 600;

const FORBIDDEN = 'Forbidden';

export default class BackendService {
  static async get(url, options = {}) {
    const fetchOptions = assign(options, { method: 'GET' });
    const cacheKey = BackendService.getFullUrl(url, fetchOptions, false);
    const cachingEnabled = fetchOptions.cache;

    // Check if value is already fetched
    if (cachingEnabled) {
      const cachedResponse = Cache.get(cacheKey);
      if (cachedResponse) {
        return cachedResponse;
      }
    }
    const response = await BackendService.callFetchWithLegacyTransformations(
      url,
      fetchOptions,
    );

    if (cachingEnabled) {
      Cache.put(cacheKey, response);
    }
    return response;
  }

  static post(url, options, noRequestHeaders) {
    const fetchOptions = merge(
      {
        method: 'POST',
        headers: options?.formData ? {} : cloneDeep(CONTENT_TYPE_HEADER),
      },
      options,
    );
    if (noRequestHeaders) {
      delete fetchOptions.headers;
    }
    return BackendService.callFetchWithLegacyTransformations(url, fetchOptions);
  }

  // Create as if we were creating online
  static postWithoutToken(url, dealerId, options) {
    const fetchOptions = merge(
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${dealerId}`,
        },
      },
      options,
    );
    return BackendService.callFetchWithLegacyTransformations(
      url,
      fetchOptions,
      false,
    );
  }

  static put(url, options, noRequestHeaders) {
    const fetchOptions = merge(
      {
        method: 'PUT',
        headers: cloneDeep(CONTENT_TYPE_HEADER),
      },
      options,
    );
    if (noRequestHeaders) {
      delete fetchOptions.headers;
    }
    return BackendService.callFetchWithLegacyTransformations(url, fetchOptions);
  }

  static patch(url, options, noRequestHeaders) {
    const fetchOptions = merge(
      {
        method: 'PATCH',
        headers: cloneDeep(CONTENT_TYPE_HEADER),
      },
      options,
    );
    if (noRequestHeaders) {
      delete fetchOptions.headers;
    }
    return BackendService.callFetchWithLegacyTransformations(url, fetchOptions);
  }

  static delete(url, options) {
    const fetchOptions = assign(options, {
      method: 'DELETE',
      headers: cloneDeep(CONTENT_TYPE_HEADER),
    });
    return BackendService.callFetchWithLegacyTransformations(url, fetchOptions);
  }

  static getFullUrl = (url, options, useToken = true) => {
    let fullUrl = BACKEND_URL;
    if (url) {
      fullUrl += url;
    }

    const params = options.params || {};
    if (useToken) {
      const token = getToken();
      if (token) {
        params.access_token = token;
      }
    }

    fullUrl += qs.stringify(params, true);
    return fullUrl;
  };

  static getFetchOptions = (input, options = {}, useToken = true) => {
    const dealIdHeader = getDealIdHeader();
    const headers = { ...DEFAULT_HEADERS };
    if (useToken) {
      const token = getToken();
      if (token) {
        Object.assign(headers, dealIdHeader, {
          'x-uar-access-token': `Bearer ${token}`,
        });
      }
    }
    Object.assign(headers, options.headers);
    if (input instanceof Request) {
      Object.assign(headers, Object.fromEntries(input.headers));
    }

    return {
      headers,
      method: options.method,
      body: options.body,
      credentials: 'include',
    };
  };

  static getFetchOptionsWithLegacyTransformations(options) {
    const fetchOptions = { ...options };
    if (options.body) {
      fetchOptions.body = JSON.stringify(options.body, (_, value) =>
        typeof value === 'undefined' ? null : value,
      );
    }
    if (options.formData) {
      fetchOptions.body = options.formData;
    }
    return fetchOptions;
  }

  static async callFetchWithLegacyTransformations(
    url,
    options,
    useToken = true,
  ) {
    try {
      const fullUrl = BackendService.getFullUrl(url, options, false);
      const fetchOptions =
        BackendService.getFetchOptionsWithLegacyTransformations(options);
      const response = await BackendService.callFetch(
        fullUrl,
        fetchOptions,
        useToken,
      );

      if (
        !!options.params &&
        !!options.params.type &&
        options.params.type === 'blob'
      ) {
        // If type is present and equals 'blob'
        // return resolved promise with blob
        const blob = await response.blob();
        return response.ok ? Promise.resolve(blob) : Promise.reject(blob);
      }

      if (options.responseType === 'text' && response.ok) {
        return await response.text();
      }

      // Default return method resolves promise with JSON
      if (response.ok) {
        const json = response.json();
        return Promise.resolve(json);
      }

      const error = await response.text();
      let message = '';
      try {
        const errorObject = JSON.parse(error);
        message =
          errorObject?.message ?? errorObject?.error ?? response.statusText;
      } catch (e) {
        /* NOOP */
      }

      return Promise.reject({ status: response.status, error, message }); // eslint-disable-line
    } catch (error) {
      const errorData = { error: 'An unknown error occurred' };
      return Promise.reject(errorData);
    }
  }

  // use_token identifies the request with a user
  // if true, this creates deals as if we were creating them with `papyrus`
  // if false, this creates deals as if we were creating them with `online`
  static async callFetch(input, options, useToken = true) {
    const fetchOptions = BackendService.getFetchOptions(
      input,
      options,
      useToken,
    );
    const response = await fetch(input, fetchOptions);

    const hasSessionDurationHeader = !!response.headers.get(
      SESSION_DURATION_HEADER,
    );
    const parsedTimeoutHeader = parseInt(
      response.headers.get(SESSION_DURATION_HEADER),
      10,
    );
    if (hasSessionDurationHeader && Number.isFinite(parsedTimeoutHeader)) {
      broadcastNewSessionTimeout(parsedTimeoutHeader);

      // if chairman is being run in an iframe inside command center, be sure to notify
      // command center of session duration changes
      forwardSessionTimeoutUpdateToCC(parsedTimeoutHeader);
    }

    if (response.ok) {
      return response;
    }

    // We should always return a usable response body, so clone before collecting
    // its contents for this error messaging
    const error = await response.clone().text();
    let message = '';
    try {
      const errorObject = JSON.parse(error);
      message =
        errorObject?.message ?? errorObject?.error ?? response.statusText;
      if (response.statusText === FORBIDDEN) {
        PageMessage({
          message,
          type: MessageType.ERROR,
        });
      }
    } catch (e) {
      /* NOOP */
    }

    try {
      const systemStatusResponse = await fetch(SYSTEM_STATUS_URL);
      if (systemStatusResponse.ok) {
        const systemStatus = await systemStatusResponse.json();
        store.dispatch(
          changeSystemStatus({
            isSystemDown:
              systemStatus.isPlannedMaintenance ||
              systemStatus.isUnplannedMaintenance ||
              systemStatus.isAcknowledgedDowntime,
            restoreDate: systemStatus.estimateTimeForNormalization,
          }),
        );
      }
    } catch (e) {
      /* NOOP */
    }

    return response;
  }
}
